import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ContractRow, ContractSaveRequest } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { sumWidth } from '@hq/ui-kit';
import { hq } from '../session';

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'default' },
  EFFECTIVE: { text: '生效中', color: 'green' },
  EXPIRED: { text: '已到期', color: 'orange' },
  TERMINATED: { text: '已终止', color: 'red' }
};

const TYPE_META: Record<string, { text: string; color: string }> = {
  FRAMEWORK: { text: '统招框架', color: 'purple' },
  SUB_SIGNED: { text: '分签', color: 'blue' },
  SPOT: { text: '零星', color: 'default' }
};

interface ContractFormValues extends Omit<ContractSaveRequest, 'signAt' | 'beginAt' | 'endAt'> {
  partyBSupplierId: string;
  range?: [Dayjs, Dayjs];
  signAtDate?: Dayjs;
}

/**
 * 合同台账（招标 R15「合同满一年后无偿使用」的关联凭证）：
 * 框架合同挂分签合同，金额上限可被预算/结算引用；只做台账，不替代法务审批。
 */
export default function ContractOps() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('ALL');
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ContractRow | null>(null);
  const [form] = Form.useForm<ContractFormValues>();
  const [parentId, setParentId] = useState<string | undefined>();

  const list = useInfiniteQuery({
    queryKey: ['contract', status, keyword],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.catalog.contracts.list({
        lastId: pageParam ?? undefined,
        size: PAGE_SIZE,
        status: status === 'ALL' ? undefined : status,
        keyword: keyword || undefined
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const suppliers = useQuery({
    queryKey: ['catalog', 'supplier', 'options'],
    queryFn: () => hq.catalog.suppliers({ size: 200 }),
    staleTime: 5 * 60 * 1000
  });

  const frameworks = useQuery({
    queryKey: ['contract', 'frameworks'],
    queryFn: () => hq.catalog.contracts.list({ size: 100, status: 'EFFECTIVE' }),
    staleTime: 60 * 1000
  });

  const save = useMutation({
    mutationFn: (values: ContractFormValues) =>
      hq.catalog.contracts.save({
        ...values,
        id: editing?.id,
        signAt: values.signAtDate?.format('YYYY-MM-DD') ?? null,
        beginAt: values.range?.[0]?.format('YYYY-MM-DD') ?? null,
        endAt: values.range?.[1]?.format('YYYY-MM-DD') ?? null,
        amountLimit: values.amountLimit ?? null
      }),
    onSuccess: async () => {
      message.success(editing ? '合同已更新' : '合同已登记');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['contract'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const openForm = (row?: ContractRow) => {
    setEditing(row ?? null);
    setParentId(row?.parentContractId ?? undefined);
    form.resetFields();
    if (row) {
      form.setFieldsValue({
        contractNo: row.contractNo,
        contractName: row.contractName,
        partyBSupplierId: row.partyBSupplierId,
        contractType: row.contractType ?? 'SPOT',
        status: row.status ?? 'DRAFT',
        amountLimit: row.amountLimit ?? undefined,
        fileUrl: row.fileUrl ?? '',
        remark: row.remark ?? '',
        parentContractId: row.parentContractId && row.parentContractId !== '0' ? row.parentContractId : undefined,
        signAtDate: row.signAt ? dayjs(row.signAt) : undefined,
        range: [row.beginAt ? dayjs(row.beginAt) : dayjs(), row.endAt ? dayjs(row.endAt) : dayjs().add(1, 'year')]
      });
    } else {
      form.setFieldsValue({ contractType: 'SPOT', status: 'DRAFT', range: [dayjs(), dayjs().add(1, 'year')] });
    }
    setOpen(true);
  };

  const columns: ColumnsType<ContractRow> = [
    {
      title: '合同号',
      dataIndex: 'contractNo',
      width: 190,
      render: (value: string, row) => <Typography.Link onClick={() => openForm(row)}>{value}</Typography.Link>
    },
    { title: '合同名称', dataIndex: 'contractName', width: 240, ellipsis: true },
    {
      title: '类型',
      dataIndex: 'contractType',
      width: 110,
      render: (value: string | null) => {
        const meta = TYPE_META[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '乙方供应商', dataIndex: 'partyBSupplierName', width: 200, ellipsis: true, render: (v: string | null) => v || '-' },
    { title: '金额上限', dataIndex: 'amountLimit', width: 140, align: 'right', render: (v: number | null) => (v == null ? '-' : formatAmount(v)) },
    { title: '生效起', dataIndex: 'beginAt', width: 120, render: (v: string | null) => formatDisplayDate(v) },
    { title: '到期日', dataIndex: 'endAt', width: 120, render: (v: string | null) => formatDisplayDate(v) },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string | null) => {
        const meta = STATUS_META[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '分签',
      dataIndex: 'childCount',
      width: 80,
      align: 'right',
      render: (value: number) => (value > 0 ? `${value} 份` : '-')
    },
    {
      title: '操作',
      key: 'action',
      width: 96,
      fixed: 'right',
      render: (_v, row) => (
        <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openForm(row)}>
          编辑
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          合同台账
          <Typography.Text type="secondary">统招框架 / 分签 / 零星合同，含 R15 无偿使用关联凭证</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            allowClear
            placeholder="合同号 / 名称"
            style={{ width: 220 }}
            onSearch={(value) => setKeyword(value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm()}>
            登记合同
          </Button>
        </Space>
      }
    >
      <Segmented
        value={status}
        onChange={(value) => setStatus(String(value))}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '草稿', value: 'DRAFT' },
          { label: '生效中', value: 'EFFECTIVE' },
          { label: '已到期', value: 'EXPIRED' },
          { label: '已终止', value: 'TERMINATED' }
        ]}
        style={{ marginBottom: 12 }}
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="合同台账加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<ContractRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: list.isError ? '加载失败，请重试' : '暂无合同' }}
        onRow={(row) => ({ onDoubleClick: () => openForm(row) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 份</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title={editing ? '编辑合同' : '登记合同'}
        open={open}
        onCancel={() => setOpen(false)}
        okText="保存"
        cancelText="取消"
        confirmLoading={save.isPending}
        onOk={() => form.submit()}
        width={720}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="contractNo" label="合同号" rules={[{ required: true, message: '必填' }]}>
                <Input maxLength={64} disabled={Boolean(editing)} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contractName" label="合同名称" rules={[{ required: true, message: '必填' }]}>
                <Input maxLength={128} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="partyBSupplierId" label="乙方供应商" rules={[{ required: true, message: '必选' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={suppliers.isPending}
                  options={(suppliers.data?.items ?? []).map((row) => ({
                    value: row.id,
                    label: `${row.supplierName}（${row.supplierCode}）`
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="contractType" label="合同类型">
                <Select
                  options={[
                    { value: 'FRAMEWORK', label: '统招框架' },
                    { value: 'SUB_SIGNED', label: '分签' },
                    { value: 'SPOT', label: '零星' }
                  ]}
                  onChange={(value) => {
                    if (value !== 'SUB_SIGNED') {
                      setParentId(undefined);
                      form.setFieldValue('parentContractId', undefined);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="status" label="状态">
                <Select
                  options={[
                    { value: 'DRAFT', label: '草稿' },
                    { value: 'EFFECTIVE', label: '生效中' },
                    { value: 'EXPIRED', label: '已到期' },
                    { value: 'TERMINATED', label: '已终止' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="parentContractId" label="所属框架合同（分签时选）">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={parentId}
                  onChange={(value) => setParentId(value)}
                  options={(frameworks.data?.items ?? []).map((row) => ({
                    value: row.id,
                    label: `${row.contractNo} ${row.contractName}`
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="signAtDate" label="签订日">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="range" label="合同期" rules={[{ required: true, message: '请选择合同期' }]}>
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amountLimit" label="金额上限（元）">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="fileUrl" label="合同扫描件 URL">
                <Input maxLength={255} placeholder="OSS / COS 链接" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remark" label="备注">
                <Input.TextArea rows={2} maxLength={255} showCount />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}
